import { useState, type FormEvent } from 'react';
import { toast } from 'sonner';

import { Alert, AlertDescription } from '@/ui/base/alert';
import { Button } from '@/ui/base/button';
import { Input } from '@/ui/base/input';
import { Label } from '@/ui/base/label';
import { RadioGroup, RadioGroupItem } from '@/ui/base/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import { ResponsiveModal } from '@/ui/ResponsiveModal';

import { mensagemDoErro, useOpcoesDeMarca } from '../parametrizacao/cadastros.dados';
import type { DecisaoDaFila, ItemDaFila } from './contrato';
import { useDecidirDaFila, useModelosEVersoesDaMarca } from './dados';

type Acao = 'GRAFIA' | 'NOVO';
type NivelNovo = 'MODELO' | 'VERSAO';

/** Valor do seletor de "de quem é esta grafia": `modelo:<id>` ou `versao:<id>`. */
type Alvo = `${'modelo' | 'versao'}:${string}`;

const NOVO_MODELO = 'novo-modelo';

interface Props {
  item: ItemDaFila;
  aoFechar: () => void;
}

/**
 * Decisão sobre um texto da fila de grafias. A pessoa diz se o texto é outra
 * grafia de um item que já existe ou um item novo — nada vira cadastro sem
 * essa escolha. Marca desconhecida decide-se primeiro; o modelo, depois, quando
 * o texto voltar à fila com a marca já reconhecida.
 */
export function DecisaoDaFilaDialogo({ item, aoFechar }: Props) {
  const ehMarca = item.pendencia === 'MARCA';
  const [acao, setAcao] = useState<Acao>('GRAFIA');
  const [marcaId, setMarcaId] = useState('');
  const [alvo, setAlvo] = useState<Alvo | ''>('');
  const [nivelNovo, setNivelNovo] = useState<NivelNovo>('MODELO');
  const [nome, setNome] = useState('');
  const [modeloDaVersao, setModeloDaVersao] = useState('');
  const [nomeDoModelo, setNomeDoModelo] = useState('');
  const [problema, setProblema] = useState<string | null>(null);

  const marcas = useOpcoesDeMarca(ehMarca);
  const daMarca = useModelosEVersoesDaMarca(ehMarca ? null : item.marcaId);
  const decidir = useDecidirDaFila();

  const trecho = ehMarca ? item.marcaTexto : item.restoTexto;

  function montar(): DecisaoDaFila | string {
    if (ehMarca) {
      if (acao === 'GRAFIA')
        return marcaId ? { acao, nivel: 'MARCA', marcaId } : 'Escolha a marca.';
      return nome.trim()
        ? { acao, nivel: 'MARCA', nome: nome.trim() }
        : 'Informe o nome oficial da marca.';
    }
    if (acao === 'GRAFIA') {
      if (!alvo) return 'Escolha o modelo ou a versão.';
      const [tipo, id] = alvo.split(':') as ['modelo' | 'versao', string];
      return tipo === 'modelo'
        ? { acao, nivel: 'MODELO', modeloId: id }
        : { acao, nivel: 'VERSAO', versaoId: id };
    }
    if (!nome.trim()) {
      return nivelNovo === 'MODELO'
        ? 'Informe o nome oficial do modelo.'
        : 'Informe o nome oficial da versão.';
    }
    if (nivelNovo === 'MODELO') return { acao, nivel: 'MODELO', nome: nome.trim() };
    if (!modeloDaVersao) return 'Escolha o modelo da versão.';
    if (modeloDaVersao === NOVO_MODELO) {
      return nomeDoModelo.trim()
        ? { acao, nivel: 'VERSAO', nome: nome.trim(), nomeDoModelo: nomeDoModelo.trim() }
        : 'Informe o nome oficial do modelo novo.';
    }
    return { acao, nivel: 'VERSAO', nome: nome.trim(), modeloId: modeloDaVersao };
  }

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    const decisao = montar();
    if (typeof decisao === 'string') {
      setProblema(decisao);
      return;
    }
    setProblema(null);
    decidir.mutate(
      { id: item.id, decisao },
      {
        onSuccess: (r) => {
          toast.success(
            r.reconhecidos > 1
              ? `Conferido. ${r.reconhecidos} textos da fonte foram reconhecidos.`
              : 'Conferido. O texto foi reconhecido.',
          );
          aoFechar();
        },
        onError: (erro) =>
          setProblema(mensagemDoErro(erro, 'Não foi possível gravar a decisão. Tente de novo.')),
      },
    );
  }

  const modelos = daMarca.data?.modelos ?? [];
  const versoes = daMarca.data?.versoes ?? [];

  return (
    <ResponsiveModal
      open
      onOpenChange={(aberto) => !aberto && aoFechar()}
      title="Conferir grafia"
      description={
        ehMarca
          ? 'A fonte usa uma marca que o TEG ainda não reconhece.'
          : `A marca ${item.marcaNome ?? ''} é conhecida, mas o modelo deste texto não.`
      }
      desktopMaxWidth="max-w-xl"
      footer={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={aoFechar} disabled={decidir.isPending}>
            Cancelar
          </Button>
          <Button
            type="submit"
            form="formulario-decisao-da-fila"
            disabled={decidir.isPending}
            aria-busy={decidir.isPending}
          >
            {decidir.isPending ? 'Gravando…' : 'Gravar decisão'}
          </Button>
        </div>
      }
    >
      <form id="formulario-decisao-da-fila" noValidate onSubmit={enviar} className="space-y-5">
        <dl className="grid gap-2 rounded-md border bg-muted/40 p-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs text-muted-foreground">Texto na fonte</dt>
            <dd className="font-mono">{item.textoOriginal}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">
              {ehMarca ? 'Marca a conferir' : 'Modelo ou versão a conferir'}
            </dt>
            <dd className="font-mono">{trecho ?? '—'}</dd>
          </div>
        </dl>

        {problema && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{problema}</AlertDescription>
          </Alert>
        )}

        <fieldset className="space-y-2">
          <legend className="text-sm font-medium">O que é este texto?</legend>
          <RadioGroup
            value={acao}
            onValueChange={(v) => {
              setAcao(v as Acao);
              setProblema(null);
            }}
          >
            <div className="flex items-center gap-2">
              <RadioGroupItem id="decisao-grafia" value="GRAFIA" />
              <Label htmlFor="decisao-grafia" className="font-normal">
                Outra grafia de um item que já existe
              </Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem id="decisao-novo" value="NOVO" />
              <Label htmlFor="decisao-novo" className="font-normal">
                {ehMarca ? 'Uma marca nova' : 'Um item novo'}
              </Label>
            </div>
          </RadioGroup>
        </fieldset>

        {ehMarca && acao === 'GRAFIA' && (
          <div className="space-y-1.5">
            <Label htmlFor="decisao-marca">Marca</Label>
            <Select value={marcaId || undefined} onValueChange={setMarcaId}>
              <SelectTrigger id="decisao-marca">
                <SelectValue placeholder={marcas.isLoading ? 'Carregando…' : 'Selecione…'} />
              </SelectTrigger>
              <SelectContent>
                {(marcas.data?.itens ?? []).map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {String(m.nome)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {marcas.isError && (
              <p className="text-xs text-destructive-text">
                Não foi possível carregar as marcas. Feche e abra de novo.
              </p>
            )}
          </div>
        )}

        {!ehMarca && acao === 'GRAFIA' && (
          <div className="space-y-1.5">
            <Label htmlFor="decisao-alvo">Modelo ou versão</Label>
            <Select value={alvo || undefined} onValueChange={(v) => setAlvo(v as Alvo)}>
              <SelectTrigger id="decisao-alvo">
                <SelectValue placeholder={daMarca.isLoading ? 'Carregando…' : 'Selecione…'} />
              </SelectTrigger>
              <SelectContent>
                {modelos.map((m) => (
                  <SelectItem key={m.id} value={`modelo:${m.id}`}>
                    Modelo {String(m.nome)}
                  </SelectItem>
                ))}
                {versoes.map((v) => (
                  <SelectItem key={v.id} value={`versao:${v.id}`}>
                    Versão {String(v.modeloNome)} {String(v.nome)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {daMarca.data && modelos.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Esta marca ainda não tem modelo ativo. Escolha &quot;Um item novo&quot;.
              </p>
            )}
            {daMarca.isError && (
              <p className="text-xs text-destructive-text">
                Não foi possível carregar os modelos. Feche e abra de novo.
              </p>
            )}
          </div>
        )}

        {!ehMarca && acao === 'NOVO' && (
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Item novo</legend>
            <RadioGroup value={nivelNovo} onValueChange={(v) => setNivelNovo(v as NivelNovo)}>
              <div className="flex items-center gap-2">
                <RadioGroupItem id="decisao-novo-modelo" value="MODELO" />
                <Label htmlFor="decisao-novo-modelo" className="font-normal">
                  Modelo novo
                </Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem id="decisao-nova-versao" value="VERSAO" />
                <Label htmlFor="decisao-nova-versao" className="font-normal">
                  Versão nova
                </Label>
              </div>
            </RadioGroup>
          </fieldset>
        )}

        {!ehMarca && acao === 'NOVO' && nivelNovo === 'VERSAO' && (
          <div className="space-y-1.5">
            <Label htmlFor="decisao-modelo-da-versao">Modelo da versão</Label>
            <Select value={modeloDaVersao || undefined} onValueChange={setModeloDaVersao}>
              <SelectTrigger id="decisao-modelo-da-versao">
                <SelectValue placeholder={daMarca.isLoading ? 'Carregando…' : 'Selecione…'} />
              </SelectTrigger>
              <SelectContent>
                {modelos.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {String(m.nome)}
                  </SelectItem>
                ))}
                <SelectItem value={NOVO_MODELO}>Um modelo novo</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {!ehMarca &&
          acao === 'NOVO' &&
          nivelNovo === 'VERSAO' &&
          modeloDaVersao === NOVO_MODELO && (
            <div className="space-y-1.5">
              <Label htmlFor="decisao-nome-do-modelo">Nome oficial do modelo</Label>
              <Input
                id="decisao-nome-do-modelo"
                maxLength={120}
                value={nomeDoModelo}
                onChange={(e) => setNomeDoModelo(e.target.value)}
              />
            </div>
          )}

        {acao === 'NOVO' && (
          <div className="space-y-1.5">
            <Label htmlFor="decisao-nome">
              {ehMarca
                ? 'Nome oficial da marca'
                : nivelNovo === 'MODELO'
                  ? 'Nome oficial do modelo'
                  : 'Nome oficial da versão'}
            </Label>
            <Input
              id="decisao-nome"
              maxLength={120}
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              aria-describedby="decisao-nome-ajuda"
            />
            <p id="decisao-nome-ajuda" className="text-xs text-muted-foreground">
              Escreva o nome completo, como deve aparecer no sistema (por exemplo, Volkswagen, e não
              VW). O texto da fonte fica guardado como grafia equivalente.
            </p>
          </div>
        )}
      </form>
    </ResponsiveModal>
  );
}
